import { useState, useEffect, useMemo } from 'react'
import { Typography, Breadcrumb, Select, Card, Row, Col, Statistic, Space, Button, Input, Table, message } from 'antd'
import { RocketOutlined, CloudDownloadOutlined, DownloadOutlined, FilterOutlined } from '@ant-design/icons'
import { useRecvizPeriods, useGenerateRecviz } from '@/hooks/useRecviz'
import type { RecvizRow } from '@/api/recviz'

const { Title } = Typography
const { Search } = Input

export default function RecvizResultPage() {
  const [selectedPeriod, setSelectedPeriod] = useState<string>('')
  const [filters, setFilters] = useState<{
    fil_name?: string
    serv_name?: string
    search?: string
  }>({})
  const [fullData, setFullData] = useState<RecvizRow[]>([])

  // Загрузка данных
  const { data: periods, isLoading: loadingPeriods } = useRecvizPeriods()

  const generateRecvizMutation = useGenerateRecviz()

  // Установить первый период по умолчанию
  useEffect(() => {
    if (periods && periods.length > 0 && !selectedPeriod) {
      setSelectedPeriod(periods[0].period)
    }
  }, [periods, selectedPeriod])

  // Обработчик формирования справочника
  const handleGenerateRecviz = async (exportToCh: boolean = false) => {
    if (!selectedPeriod) {
      message.warning('Выберите период')
      return
    }

    try {
      const result = await generateRecvizMutation.mutateAsync({
        period: selectedPeriod,
        export_to_ch: exportToCh,
      })

      // Сохраняем полные данные (в реальности нужен отдельный endpoint для полного списка)
      // Пока используем preview как демо
      setFullData(result.preview)

      if (exportToCh && result.export_status?.success) {
        message.success(`Экспортировано ${result.export_status.rows_exported} записей в ${result.export_status.table}`)
      } else {
        message.success(`Справочник сформирован: ${result.rows_count} записей`)
      }
    } catch (error) {
      message.error('Ошибка при формировании справочника')
    }
  }

  // Фильтрация данных
  const filteredData = useMemo(() => {
    let result = fullData

    if (filters.fil_name) {
      result = result.filter(row => row.fil_name === filters.fil_name)
    }

    if (filters.serv_name) {
      result = result.filter(row => row.serv_name === filters.serv_name)
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      result = result.filter(row =>
        row.code_otd_name_full?.toLowerCase().includes(searchLower) ||
        row.podr_name_full?.toLowerCase().includes(searchLower) ||
        row.id_otd?.toLowerCase().includes(searchLower)
      )
    }

    return result
  }, [fullData, filters])

  // Статистика
  const stats = useMemo(() => {
    const totalRecords = filteredData.length
    const boundFilials = new Set(filteredData.filter(r => r.fil_name).map(r => r.fil_name)).size
    const boundServices = new Set(filteredData.filter(r => r.serv_name).map(r => r.serv_name)).size

    return {
      totalRecords,
      boundFilials,
      boundServices,
    }
  }, [filteredData])

  // Опции для фильтров
  const filialOptions = useMemo(() => {
    const uniqueFilials = new Set(fullData.map(r => r.fil_name).filter(Boolean))
    return Array.from(uniqueFilials).map(name => ({ value: name, label: name }))
  }, [fullData])

  const serviceOptions = useMemo(() => {
    const uniqueServices = new Set(fullData.map(r => r.serv_name).filter(Boolean))
    return Array.from(uniqueServices).map(name => ({ value: name, label: name }))
  }, [fullData])

  // Экспорт в CSV
  const handleDownloadCSV = () => {
    if (filteredData.length === 0) {
      message.warning('Нет данных для экспорта')
      return
    }

    // Формируем CSV
    const headers = Object.keys(filteredData[0])
    const csvContent = [
      headers.join(','),
      ...filteredData.map(row => headers.map(h => row[h] ?? '').join(','))
    ].join('\n')

    // Скачиваем
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `recviz_${selectedPeriod}.csv`
    link.click()
    URL.revokeObjectURL(link.href)

    message.success('CSV файл скачан')
  }

  // Колонки таблицы (все 28 полей)
  const columns = [
    {
      title: 'Период',
      dataIndex: 'ot_per',
      key: 'ot_per',
      width: 100,
      fixed: 'left' as const,
    },
    {
      title: 'ID отделения',
      dataIndex: 'id_otd',
      key: 'id_otd',
      width: 120,
      fixed: 'left' as const,
    },
    {
      title: 'Подразделение',
      dataIndex: 'podr_name_full',
      key: 'podr_name_full',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Наименование отделения',
      dataIndex: 'code_otd_name_full',
      key: 'code_otd_name_full',
      width: 250,
      ellipsis: true,
    },
    {
      title: 'Филиал',
      dataIndex: 'fil_name',
      key: 'fil_name',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Служба',
      dataIndex: 'serv_name',
      key: 'serv_name',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Коек (факт)',
      dataIndex: 'kf_total_fact',
      key: 'kf_total_fact',
      width: 120,
      align: 'right' as const,
    },
    {
      title: 'Коек (приказ)',
      dataIndex: 'kf_total_prikaz',
      key: 'kf_total_prikaz',
      width: 120,
      align: 'right' as const,
    },
    {
      title: 'План случаи',
      dataIndex: 'cases_plan_gen',
      key: 'cases_plan_gen',
      width: 120,
      align: 'right' as const,
    },
    // Добавьте остальные поля по мере необходимости
  ]

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>Оргструктура и реквизиты</Breadcrumb.Item>
        <Breadcrumb.Item>Справочник реквизитов</Breadcrumb.Item>
      </Breadcrumb>

      <Title level={2}>Справочник реквизитов (полный просмотр)</Title>

      {/* Контролы и фильтры */}
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* Период и действия */}
          <Space size="large" style={{ width: '100%', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', marginBottom: 8 }}>Период:</label>
              <Select
                style={{ width: 200 }}
                placeholder="Выберите период"
                value={selectedPeriod}
                onChange={setSelectedPeriod}
                loading={loadingPeriods}
                options={periods?.map(p => ({ value: p.period, label: p.label || p.period })) || []}
              />
            </div>

            <div style={{ marginLeft: 'auto' }}>
              <label style={{ display: 'block', marginBottom: 8 }}>&nbsp;</label>
              <Space>
                <Button
                  type="primary"
                  icon={<RocketOutlined />}
                  onClick={() => handleGenerateRecviz(false)}
                  loading={generateRecvizMutation.isPending}
                  disabled={!selectedPeriod}
                >
                  Сформировать
                </Button>
                <Button
                  icon={<CloudDownloadOutlined />}
                  onClick={() => handleGenerateRecviz(true)}
                  loading={generateRecvizMutation.isPending}
                  disabled={!selectedPeriod}
                >
                  Экспорт в ClickHouse
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadCSV}
                  disabled={filteredData.length === 0}
                >
                  Скачать CSV
                </Button>
              </Space>
            </div>
          </Space>

          {/* Фильтры */}
          <Space size="large" style={{ width: '100%', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', marginBottom: 8 }}>
                <FilterOutlined /> Филиал:
              </label>
              <Select
                style={{ width: 200 }}
                placeholder="Все"
                allowClear
                value={filters.fil_name}
                onChange={(value) => setFilters({ ...filters, fil_name: value })}
                options={filialOptions}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8 }}>
                <FilterOutlined /> Служба:
              </label>
              <Select
                style={{ width: 200 }}
                placeholder="Все"
                allowClear
                value={filters.serv_name}
                onChange={(value) => setFilters({ ...filters, serv_name: value })}
                options={serviceOptions}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8 }}>Поиск:</label>
              <Search
                placeholder="Поиск по наименованию"
                allowClear
                style={{ width: 300 }}
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
          </Space>
        </Space>
      </Card>

      {/* Статистика */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Всего записей"
              value={stats.totalRecords}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Привязано филиалов"
              value={stats.boundFilials}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Привязано служб"
              value={stats.boundServices}
            />
          </Card>
        </Col>
      </Row>

      {/* Таблица */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id_otd"
          loading={generateRecvizMutation.isPending}
          scroll={{ x: 'max-content', y: 600 }}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (total) => `Всего: ${total} записей`,
          }}
        />
      </Card>
    </div>
  )
}
